import { useEffect } from 'react'
import { Transition } from '@headlessui/react'
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/20/solid'

// Message format: "variant:title:text"
// variant: success (default) | warning | error
// Examples:
//   "Saved:customer po"          → green check, "Saved", "customer po"
//   "warning:Warning:Excess"     → orange triangle, "Warning", "Excess"
//   "error:Error:Not found"      → red X, "Error", "Not found"

interface NotificationProps {
  message: string
  show: boolean
  onClose: () => void
  autoHideMs?: number
}

function titleCase(s: string) { return s.replace(/\b\w/g, c => c.toUpperCase()) }

function parseMessage(message: string) {
  const parts = message.split(':')
  if (parts[0] === 'error') return { variant: 'error' as const, title: titleCase(parts[1] || 'Error'), text: titleCase(parts.slice(2).join(':') || parts[1] || '') }
  if (parts[0] === 'warning') return { variant: 'warning' as const, title: titleCase(parts[1] || 'Warning'), text: titleCase(parts.slice(2).join(':') || parts[1] || '') }
  if (parts.length >= 2) return { variant: 'success' as const, title: titleCase(parts[0]), text: titleCase(parts.slice(1).join(':')) }
  return { variant: 'success' as const, title: 'Success', text: titleCase(message) }
}

const icons = {
  success: <CheckCircleIcon aria-hidden="true" className="size-6 text-green-400" />,
  warning: <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-amber-500" />,
  error: <XCircleIcon aria-hidden="true" className="size-6 text-red-500" />,
}

export default function Notification({ message, show, onClose, autoHideMs = 2500 }: NotificationProps) {
  const { variant, title, text } = parseMessage(message)

  useEffect(() => {
    if (!show) return
    const timer = setTimeout(onClose, autoHideMs)
    return () => clearTimeout(timer)
  }, [show, onClose, autoHideMs])

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <Transition show={show}>
          <div className="pointer-events-auto w-full max-w-sm rounded-lg bg-white shadow-lg outline-1 outline-black/5 transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-leave:duration-100 data-leave:ease-in data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0 dark:bg-zinc-800 dark:outline-white/10">
            <div className="p-4">
              <div className="flex items-start">
                <div className="shrink-0">{icons[variant]}</div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
                  {text && <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">{text}</p>}
                </div>
                <div className="ml-4 flex shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon aria-hidden="true" className="size-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  )
}
